import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus,
  X,
  Save,
  AlertCircle,
  CheckCircle2,
  Settings,
  Wrench
} from 'lucide-react';
import { 
  enableAgent, 
  disableAgent,
  addAllowedTool,
  removeAllowedTool,
  addDeniedTool,
  setToolDailyLimit 
} from '@/lib/policyApi';
import { toast } from 'sonner';

const AgentPolicyCard = ({ hotelId, agentName, agentInfo, rules, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newToolName, setNewToolName] = useState('');
  const [toolLimits, setToolLimits] = useState({});
  const [loading, setLoading] = useState(false);

  const Icon = agentInfo.icon;

  const handleToggleAgent = async (enabled) => {
    try {
      setLoading(true);
      if (enabled) {
        await enableAgent(hotelId, agentName);
        toast.success(`${agentInfo.name} enabled successfully`);
      } else {
        await disableAgent(hotelId, agentName);
        toast.success(`${agentInfo.name} disabled successfully`);
      }
      await onUpdate();
    } catch (error) {
      console.error('Error toggling agent:', error);
      toast.error(`Failed to ${enabled ? 'enable' : 'disable'} agent`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTool = async () => {
    if (!newToolName.trim()) {
      toast.error('Please enter a tool name');
      return;
    }

    try {
      setLoading(true);
      await addAllowedTool(hotelId, agentName, newToolName.trim());
      toast.success(`Added ${newToolName} to allowed tools`);
      setNewToolName('');
      await onUpdate();
    } catch (error) {
      console.error('Error adding tool:', error);
      toast.error('Failed to add tool');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTool = async (toolName) => {
    try {
      setLoading(true);
      await removeAllowedTool(hotelId, agentName, toolName);
      toast.success(`Removed ${toolName} from allowed tools`);
      await onUpdate();
    } catch (error) {
      console.error('Error removing tool:', error);
      toast.error('Failed to remove tool');
    } finally {
      setLoading(false);
    }
  };

  const handleDenyTool = async (toolName) => {
    try {
      setLoading(true);
      await addDeniedTool(hotelId, agentName, toolName);
      toast.success(`Added ${toolName} to denied tools`);
      await onUpdate();
    } catch (error) {
      console.error('Error denying tool:', error);
      toast.error('Failed to deny tool');
    } finally {
      setLoading(false);
    }
  };

  const handleSetToolLimit = async (toolName) => {
    const limit = toolLimits[toolName];
    if (!limit || limit < 1) {
      toast.error('Please enter a valid limit (minimum 1)');
      return;
    }

    try {
      setLoading(true);
      await setToolDailyLimit(hotelId, agentName, toolName, parseInt(limit));
      toast.success(`Set daily limit for ${toolName} to ${limit}`);
      setToolLimits({ ...toolLimits, [toolName]: '' });
      await onUpdate();
    } catch (error) {
      console.error('Error setting tool limit:', error);
      toast.error('Failed to set tool limit');
    } finally {
      setLoading(false);
    }
  };

  const availableTools = {
    bitsy: ['log_concierge_request', 'lookup_booking', 'create_maintenance_ticket'],
    marina: ['lookup_booking', 'modify_booking', 'create_maintenance_ticket'],
    sage: ['create_maintenance_ticket', 'lookup_booking', 'modify_booking']
  };

  const toolDescriptions = {
    'log_concierge_request': 'Log guest requests for concierge services',
    'lookup_booking': 'View booking details and reservation information',
    'modify_booking': 'Change booking dates, room type, or special requests',
    'create_maintenance_ticket': 'Create tickets for facility issues and repairs'
  };

  return (
    <Card className={`${agentInfo.bgColor} border-2`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg bg-white shadow-sm`}>
              <Icon className={`h-8 w-8 ${agentInfo.color}`} />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-2xl">{agentInfo.name}</CardTitle>
              <CardDescription className="text-base">
                {agentInfo.description}
              </CardDescription>
              <p className="text-sm text-gray-600 mt-2">
                {agentInfo.capabilities}
              </p>
            </div>
          </div>

          {/* Agent Enable/Disable Toggle */}
          <div className="flex items-center gap-3">
            <Label htmlFor={`${agentName}-toggle`} className="text-sm font-medium">
              {rules.enabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id={`${agentName}-toggle`}
              checked={rules.enabled}
              onCheckedChange={handleToggleAgent}
              disabled={loading}
              data-testid={`${agentName}-enable-toggle`}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        
        {/* Status Banner */}
        {!rules.enabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-900">
              This agent is currently <strong>disabled</strong>. Enable it to allow tool execution.
            </p>
          </div>
        )}

        {/* Configuration Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Allowed Tools</p>
            <p className="text-2xl font-bold text-green-600" data-testid={`${agentName}-allowed-count`}>
              {rules.allowed_tools?.length || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Denied Tools</p>
            <p className="text-2xl font-bold text-red-600">
              {rules.denied_tools?.length || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Daily Limit</p>
            <p className="text-2xl font-bold text-blue-600">
              {rules.max_daily_tool_calls || 'Unlimited'}
            </p>
          </div>
        </div>

        {/* Allowed Tools */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Allowed Tools
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
              className="gap-1"
            >
              <Settings className="h-3 w-3" />
              {isEditing ? 'Done' : 'Manage'}
            </Button>
          </div>

          {/* Tool List */}
          <div className="space-y-2">
            {rules.allowed_tools && rules.allowed_tools.length > 0 ? (
              rules.allowed_tools.map((tool) => (
                <div 
                  key={tool}
                  className="bg-white rounded-lg p-3 flex items-start justify-between gap-3 shadow-sm"
                  data-testid={`allowed-tool-${tool}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Wrench className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <code className="text-sm font-mono font-semibold text-gray-900">
                        {tool}
                      </code>
                    </div>
                    <p className="text-xs text-gray-600 pl-6">
                      {toolDescriptions[tool] || 'Custom tool'}
                    </p>

                    {/* Tool-specific limit */}
                    {rules.tool_rules && rules.tool_rules[tool] && (
                      <div className="mt-2 pl-6">
                        <Badge variant="outline" className="text-xs">
                          Limit: {rules.tool_rules[tool].max_daily_uses || 'Unlimited'} uses/day
                        </Badge>
                      </div>
                    )}

                    {/* Set limit input (when editing) */}
                    {isEditing && (
                      <div className="mt-2 pl-6 flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          placeholder="Daily limit"
                          value={toolLimits[tool] || ''}
                          onChange={(e) => setToolLimits({ ...toolLimits, [tool]: e.target.value })}
                          className="w-32 h-8 text-xs"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetToolLimit(tool)}
                          disabled={!toolLimits[tool] || loading}
                          className="h-8 text-xs"
                        >
                          Set Limit
                        </Button>
                      </div>
                    )}
                  </div>

                  {isEditing && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveTool(tool)}
                        disabled={loading}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-tool-${tool}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg p-6 text-center text-gray-500">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No allowed tools configured</p>
              </div>
            )}
          </div>

          {/* Add Tool */}
          {isEditing && (
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <Label className="text-xs text-gray-600 mb-2 block">Add Tool to Allowlist</Label>
              <div className="flex gap-2">
                <select
                  value={newToolName}
                  onChange={(e) => setNewToolName(e.target.value)}
                  className="flex-1 h-9 rounded-md border border-gray-300 px-3 text-sm"
                  data-testid="add-tool-select"
                >
                  <option value="">Select a tool...</option>
                  {availableTools[agentName].filter(t => !rules.allowed_tools?.includes(t)).map(tool => (
                    <option key={tool} value={tool}>{tool}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={handleAddTool}
                  disabled={!newToolName || loading}
                  className="gap-1"
                  data-testid="add-tool-btn"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Denied Tools */}
        {rules.denied_tools && rules.denied_tools.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <X className="h-4 w-4 text-red-600" />
              Denied Tools (Blacklist)
            </h4>
            <div className="space-y-2">
              {rules.denied_tools.map((tool) => (
                <div 
                  key={tool}
                  className="bg-red-50 rounded-lg p-3 flex items-center justify-between border border-red-200"
                >
                  <code className="text-sm font-mono font-semibold text-red-900">
                    {tool}
                  </code>
                  <Badge variant="destructive">Blocked</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {rules.notes && (
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Policy Notes</p>
            <p className="text-sm text-gray-700">{rules.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentPolicyCard;
